import { Plus, X } from 'lucide-react';
import { useId, useState } from 'react';

import { cx } from '@/lib/cx';
import { Button } from '@/ui/primitives/button';
import { Campo, Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';
import { type DiaSemana, MAXIMO_HORARIOS_CAPTURA, minutosDoHorario } from '@fiscoai/contracts';

import { DIA_CURTO, DIA_POR_EXTENSO, descreverIntervalo } from './descrever-agenda';

const SEMANA: readonly DiaSemana[] = ['SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB', 'DOM'];

function Erro({ id, texto }: { id?: string; texto: string | undefined }) {
  if (!texto) return null;
  return (
    <p id={id} role="alert" className="text-xs text-perigo">
      {texto}
    </p>
  );
}

export function SeletorDias({
  dias,
  aoMudar,
  erro,
}: {
  dias: readonly DiaSemana[];
  aoMudar: (dias: DiaSemana[]) => void;
  erro: string | undefined;
}) {
  const id = useId();
  return (
    <div className="grid gap-1.5">
      <span id={`${id}-rotulo`} className="text-sm font-medium">
        Dias
      </span>
      <div
        role="group"
        aria-labelledby={`${id}-rotulo`}
        aria-describedby={erro ? `${id}-erro` : undefined}
        className="flex flex-wrap gap-1"
      >
        {SEMANA.map((d) => {
          const marcado = dias.includes(d);
          return (
            <button
              key={d}
              type="button"
              aria-pressed={marcado}
              aria-label={DIA_POR_EXTENSO[d]}
              onClick={() => {
                aoMudar(marcado ? dias.filter((x) => x !== d) : [...dias, d]);
              }}
              className={cx(
                'h-controle-sm min-w-11 rounded-controle border px-2 text-sm font-medium transition-colors duration-[var(--duracao-rapida)]',
                'focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave',
                marcado
                  ? 'border-marca bg-marca text-marca-texto'
                  : 'border-borda-forte bg-superficie text-texto-suave hover:bg-superficie-hover',
                d === 'SAB' && 'sm:ml-2',
              )}
            >
              {DIA_CURTO[d]}
            </button>
          );
        })}
      </div>
      <Erro id={`${id}-erro`} texto={erro} />
    </div>
  );
}

export function HorariosFixos({
  horarios,
  aoMudar,
  erro,
}: {
  horarios: readonly string[];
  aoMudar: (horarios: string[]) => void;
  erro: string | undefined;
}) {
  const [novo, setNovo] = useState('');
  const id = useId();
  const ordenados = [...horarios].sort((a, b) => minutosDoHorario(a) - minutosDoHorario(b));
  const cheio = horarios.length >= MAXIMO_HORARIOS_CAPTURA;
  const adicionar = () => {
    if (!/^\d{2}:\d{2}$/.test(novo) || horarios.includes(novo)) return;
    aoMudar([...horarios, novo]);
    setNovo('');
  };
  return (
    <div className="grid gap-2">
      <ul
        aria-label="Horários das consultas"
        aria-describedby={erro ? `${id}-erro` : undefined}
        className="flex min-h-controle-sm flex-wrap gap-1.5"
      >
        {ordenados.map((h) => (
          <li
            key={h}
            className="inline-flex h-controle-sm animate-surgir items-center gap-1 rounded-pilula border border-marca/40 bg-marca-suave pr-1 pl-3 text-sm font-semibold tabular text-marca"
          >
            {h}
            <button
              type="button"
              aria-label={`Remover ${h}`}
              onClick={() => {
                aoMudar(horarios.filter((x) => x !== h));
              }}
              className="grid size-6 cursor-pointer place-items-center rounded-pilula hover:bg-superficie-hover focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-marca-suave"
            >
              <X className="size-3.5" aria-hidden />
            </button>
          </li>
        ))}
        {ordenados.length === 0 && (
          <li className="self-center text-sm text-texto-fraco">Nenhum horário ainda.</li>
        )}
      </ul>
      <div className="flex items-end gap-2">
        <Campo rotulo="Novo horário" className="w-36">
          {(a) => (
            <Input
              {...a}
              type="time"
              value={novo}
              disabled={cheio}
              onChange={(e) => {
                setNovo(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                adicionar();
              }}
              className="tabular"
            />
          )}
        </Campo>
        <Button onClick={adicionar} disabled={cheio || novo === ''}>
          <Plus aria-hidden /> Adicionar horário
        </Button>
      </div>
      <Erro id={`${id}-erro`} texto={erro} />
    </div>
  );
}

const INTERVALOS = [65, 90, 120, 180, 240, 360];
const opcoesIntervalo = (lista: readonly number[]) =>
  lista.map((m) => ({
    valor: String(m),
    rotulo: m === 65 ? '65 min (o mínimo)' : descreverIntervalo(m),
  }));

export function CampoIntervalo({
  valor,
  aoMudar,
  erro,
}: {
  valor: number;
  aoMudar: (minutos: number) => void;
  erro: string | undefined;
}) {
  return (
    <Campo rotulo="A cada" erro={erro} className="w-48">
      {(a) => (
        <Select
          {...a}
          opcoes={opcoesIntervalo(INTERVALOS.includes(valor) ? INTERVALOS : [...INTERVALOS, valor])}
          valor={String(valor)}
          aoMudar={(v) => {
            aoMudar(Number(v));
          }}
        />
      )}
    </Campo>
  );
}

export function CampoHora({
  rotulo,
  valor,
  aoMudar,
  erro,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (valor: string) => void;
  erro?: string | undefined;
}) {
  return (
    <Campo rotulo={rotulo} erro={erro} className="w-36">
      {(a) => (
        <Input
          {...a}
          type="time"
          value={valor}
          onChange={(e) => {
            aoMudar(e.target.value);
          }}
          className="tabular"
        />
      )}
    </Campo>
  );
}
